import { useCallback, useEffect, useRef } from 'react'
import Box, { BoxProps } from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CountUp from 'react-countup'
import Button from '@mui/material/Button'
import SvgIcon from '@mui/material/SvgIcon'

import { useDApp } from 'contexts/Web3'
import ConnectButton from './ConnectButton'
import formatWei from 'utils/formatWei'

import { TT as SvgTT } from 'components/Icons/SvgIcon'
import { PMT as PngPMT } from 'components/Icons/PngIcon'

const iconTT = (<SvgIcon component={SvgTT} inheritViewBox />)
const iconPMT = (<PngPMT sx={{ width: '1em', height: '1em' }} />)

const formatCurrency = formatWei()

export default function Header(props: BoxProps) {
  const { configs, wallet, balances } = useDApp()
  const display = useRef<{ [token: string]: { start: number, end: number }}>({
    TT: { start: Number(balances?.TT?.display ?? '0'), end: Number(balances?.TT?.display ?? '0') },
    PMT: { start: Number(balances?.PMT?.display ?? '0'), end: Number(balances?.PMT?.display ?? '0') },
  })

  const { balance } = wallet as { balance: string }

  const addToWallet = useCallback(() => {
    const token = configs.token.PMT
    if (!token || !wallet.ethereum || !wallet.ethereum.request) return

    wallet?.ethereum?.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address: token.address,
          symbol: token.symbol,
          decimals: token.decimals,
          image: token.image,
        },
      },
    })
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [wallet.ethereum])

  useEffect(() => {
    const { TT, PMT } = display.current
    const tt = Number((balances?.TT?.display ?? formatCurrency(balance))) || 0
    const pmt = Number((balances?.PMT?.display ?? '0')) || 0

    if (TT.end !== tt) {
      display.current.TT = {
        start: TT.end,
        end: tt,
      }
    }

    if (PMT.end !== pmt) {
      display.current.PMT = {
        start: PMT.end,
        end: pmt,
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances])

  return (
    <Box
      component="header"
      display="flex"
      flexDirection="column"
      zIndex={30}
      {...props}
    >
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Box flexGrow={1} />

        {/* Connection Button */}
        <ConnectButton />
        {/* /Connection Button */}

        <Box flexBasis={4} />

        {/* Balances */}
        <Stack
          direction="column"
          paddingX={1}
          paddingY={2}
          alignItems="flex-start"
        >
          <Button startIcon={iconTT} size="small">
            <Typography variant="balance">
              <CountUp
                start={display.current.TT.start}
                end={display.current.TT.end}
                suffix=" TT"
                decimals={0}
                duration={1}
                separator=","
              />
            </Typography>
          </Button>

          <Button
            startIcon={iconPMT}
            size="small"
            onClick={addToWallet}
          >
            <Typography variant="balance">
              <CountUp
                start={display.current.PMT.start}
                end={display.current.PMT.end}
                suffix=" PMT"
                decimals={1}
                duration={1}
                separator=","
              />
            </Typography>
          </Button>
        </Stack>{/* /Balances */}

        <Box flexGrow={1} />
      </Stack>
    </Box>
  )
}
